import { getPsychologistId } from '../hooks/api/auth/tokenManager';
import { reportUnsupportedPushDevice, type PushPlatform, type UnsupportedReason } from '../hooks/api/pushNotifications';
import { normalizeVersion } from './deviceInfo';
import { detectPlatform } from './pushDevice';

/**
 * Dispositivo que NO soporta push (iOS < 16.4, navegador sin PushManager…): la app NO se bloquea, pero se avisa al backend
 * para que psima lo vea. El criterio de soporte es siempre FEATURE DETECTION; la versión del sistema sale del user agent
 * y es solo informativa (en iOS 26 Safari la congela en 18.x, así que es aproximada).
 */

/** Por qué no hay push, o `null` si el dispositivo lo soporta. El primer faltante manda. */
export function detectUnsupportedReason(): UnsupportedReason | null {
  if (!('Notification' in window)) return 'no_notification_api';
  if (!('serviceWorker' in navigator)) return 'no_service_worker';
  if (!('PushManager' in window)) return 'no_push_manager';
  return null;
}

/** Versión del sistema según el user agent (solo iOS y Android; el resto no informa). */
export function parseOsVersion(userAgent: string, platform: PushPlatform): string | undefined {
  if (platform === 'ios') {
    return normalizeVersion(/(?:iPhone OS|CPU OS|iPadOS) ([\d_]+)/i.exec(userAgent)?.[1]);
  }
  if (platform === 'android') {
    return normalizeVersion(/Android ([\d.]+)/i.exec(userAgent)?.[1]);
  }
  return undefined;
}

const REPORTED_KEY = 'psi-push-unsupported-reported';
/** Como mucho un aviso por día y por motivo: abrir la app muchas veces no satura al backend. */
export const REPORT_MIN_INTERVAL_MS = 24 * 60 * 60 * 1000;

function alreadyReportedRecently(signature: string): boolean {
  try {
    const raw = localStorage.getItem(REPORTED_KEY);
    if (!raw) return false;
    const saved = JSON.parse(raw) as { signature?: string; at?: number };
    return saved.signature === signature && typeof saved.at === 'number' && Date.now() - saved.at < REPORT_MIN_INTERVAL_MS;
  } catch {
    return false;
  }
}

function markReported(signature: string): void {
  try {
    localStorage.setItem(REPORTED_KEY, JSON.stringify({ signature, at: Date.now() }));
  } catch {
    /* noop: sin storage se avisa en cada apertura, sigue siendo idempotente en el backend */
  }
}

/** Avisa al backend, una vez por día. Nunca tira ni bloquea: sin red se reintenta la próxima vez que se abra la app. */
export async function reportUnsupportedDevice(): Promise<void> {
  try {
    const reason = detectUnsupportedReason();
    const psychologistId = getPsychologistId();
    if (!reason || !psychologistId) return;

    const platform = detectPlatform();
    const osVersion = parseOsVersion(navigator.userAgent, platform);
    const signature = `${psychologistId}|${platform}|${osVersion ?? ''}|${reason}`;
    if (alreadyReportedRecently(signature)) return;

    await reportUnsupportedPushDevice({ platform, ...(osVersion ? { osVersion } : {}), reason });
    markReported(signature);
  } catch {
    /* sin red o backend caído: no es problema de la psicóloga */
  }
}
